import {
  ArtifactAppendArgs,
  ArtifactBackend,
  ArtifactBeginArgs,
  ArtifactKeyRef,
  ArtifactManager,
  ArtifactSnapshot,
  ArtifactState,
} from "@mem-client/artifacts";
import { Table } from "dexie";
import { DateTime } from "luxon";

import type { MemDB } from "@/domains/dexie-db/db";
import type {
  LocalWebAudioArtifactChunkRecord,
  LocalWebAudioArtifactRecord,
} from "@/domains/dexie-db/models";
import { Uuid } from "@/domains/global/identifiers";
import { MediaRecorderMimeType } from "@/store/audio/recording/media-types";

import {
  WebAudioArtifactChunk,
  WebAudioArtifactMetadata,
  WebAudioArtifactRef,
  WebAudioArtifactSnapshot,
  WebAudioArtifactState,
} from "./web-audio-artifact-types";

type WebAudioArtifactServiceConfig = {
  dbRef: () => MemDB;
};

type BuildManifestArgs = ArtifactBeginArgs & {
  state: WebAudioArtifactState;
};

type DexieTables = {
  manifests: Table<LocalWebAudioArtifactRecord, string>;
  chunks: Table<LocalWebAudioArtifactChunkRecord, string>;
};

const mapArtifactState = ({
  state,
}: {
  state: ArtifactState | WebAudioArtifactState;
}): WebAudioArtifactState => {
  switch (state) {
    case ArtifactState.Finalized:
      return WebAudioArtifactState.Finalized;
    case ArtifactState.Writing:
    default:
      return WebAudioArtifactState.Writing;
  }
};

const buildArtifactRefFromManifest = ({
  manifest,
}: {
  manifest: LocalWebAudioArtifactRecord;
}): WebAudioArtifactRef => {
  return {
    artifactId: manifest.artifactId,
    key: manifest.key,
    mimeType: manifest.mimeType,
    sizeBytes: manifest.sizeBytes,
    durationMs: manifest.durationMs,
    chunkCount: manifest.chunkCount,
    metadata: manifest.metadata,
  };
};

class DexieWebAudioArtifactBackend implements ArtifactBackend<
  WebAudioArtifactChunk,
  WebAudioArtifactRef
> {
  private readonly dbRef: () => MemDB;

  constructor({ dbRef }: { dbRef: () => MemDB }) {
    this.dbRef = dbRef;
  }

  async begin({ key, mimeType, metadata }: ArtifactBeginArgs): Promise<void> {
    const tables = this.getTables();
    const now = DateTime.now();
    const manifest = this.buildManifest({
      key,
      mimeType,
      metadata,
      state: WebAudioArtifactState.Writing,
    });

    await this.dbRef().transaction("rw", [tables.manifests, tables.chunks], async () => {
      await tables.chunks.where({ artifactKey: key }).delete();
      await tables.manifests.put({
        ...manifest,
        sizeBytes: 0,
        durationMs: 0,
        chunkCount: 0,
        createdAt: now,
        updatedAt: now,
      });
    });
  }

  async append({ key, chunk }: ArtifactAppendArgs<WebAudioArtifactChunk>): Promise<void> {
    const tables = this.getTables();

    await this.dbRef().transaction("rw", [tables.manifests, tables.chunks], async () => {
      const manifest = await this.readRequiredManifest({
        key,
        tables,
      });

      if (manifest.state === WebAudioArtifactState.Finalized) {
        throw new Error(`Artifact ${key} is already finalized`);
      }

      const chunkIndex = manifest.chunkCount;
      const now = DateTime.now();
      const nextMimeType =
        manifest.chunkCount === 0 && chunk.mimeType ? chunk.mimeType : manifest.mimeType;

      await tables.chunks.put({
        id: `${key}:${chunkIndex}`,
        artifactKey: key as Uuid,
        chunkIndex,
        rawBlob: chunk.blob,
        byteLength: chunk.byteLength,
        durationMs: chunk.durationMs,
        mimeType: chunk.mimeType,
        createdAt: now,
        updatedAt: now,
      });

      await tables.manifests.put({
        ...manifest,
        mimeType: nextMimeType,
        sizeBytes: manifest.sizeBytes + chunk.byteLength,
        durationMs: manifest.durationMs + chunk.durationMs,
        chunkCount: manifest.chunkCount + 1,
        updatedAt: now,
      });
    });
  }

  async finalize({ key }: ArtifactKeyRef): Promise<WebAudioArtifactRef> {
    const tables = this.getTables();

    return this.dbRef().transaction("rw", [tables.manifests], async () => {
      const manifest = await this.readRequiredManifest({
        key,
        tables,
      });
      const nextManifest = {
        ...manifest,
        state: WebAudioArtifactState.Finalized,
        updatedAt: DateTime.now(),
      };

      await tables.manifests.put(nextManifest);

      return buildArtifactRefFromManifest({
        manifest: nextManifest,
      });
    });
  }

  async getSnapshot({
    key,
  }: ArtifactKeyRef): Promise<ArtifactSnapshot<WebAudioArtifactRef> | null> {
    const manifest = await this.getTables().manifests.get(key);
    if (!manifest) {
      return null;
    }

    return {
      ...buildArtifactRefFromManifest({
        manifest,
      }),
      state:
        manifest.state === WebAudioArtifactState.Finalized
          ? ArtifactState.Finalized
          : ArtifactState.Writing,
    };
  }

  async getRef({ key }: ArtifactKeyRef): Promise<WebAudioArtifactRef | null> {
    const snapshot = await this.getSnapshot({ key });

    if (!snapshot || snapshot.state !== ArtifactState.Finalized) {
      return null;
    }

    const { state: _state, ...artifactRef } = snapshot;
    return artifactRef;
  }

  async remove({ key }: ArtifactKeyRef): Promise<void> {
    const tables = this.getTables();

    await this.dbRef().transaction("rw", [tables.manifests, tables.chunks], async () => {
      await Promise.all([
        tables.manifests.delete(key),
        tables.chunks.where({ artifactKey: key }).delete(),
      ]);
    });
  }

  async readChunks({ key }: ArtifactKeyRef): Promise<LocalWebAudioArtifactChunkRecord[]> {
    return this.getTables().chunks.where({ artifactKey: key }).sortBy("chunkIndex");
  }

  async clearAll(): Promise<void> {
    const tables = this.getTables();

    await this.dbRef().transaction("rw", [tables.manifests, tables.chunks], async () => {
      await Promise.all([tables.manifests.clear(), tables.chunks.clear()]);
    });
  }

  private getTables(): DexieTables {
    const db = this.dbRef();

    return {
      manifests: db.table("localWebAudioArtifacts") as Table<LocalWebAudioArtifactRecord, string>,
      chunks: db.table("localWebAudioArtifactChunks") as Table<
        LocalWebAudioArtifactChunkRecord,
        string
      >,
    };
  }

  private buildManifest({
    key,
    mimeType,
    metadata,
    state,
  }: BuildManifestArgs): Omit<
    LocalWebAudioArtifactRecord,
    "sizeBytes" | "durationMs" | "chunkCount" | "createdAt" | "updatedAt"
  > {
    return {
      key: key as Uuid,
      artifactId: key,
      mimeType,
      metadata,
      state,
    };
  }

  private async readRequiredManifest({
    key,
    tables,
  }: {
    key: string;
    tables: DexieTables;
  }): Promise<LocalWebAudioArtifactRecord> {
    const manifest = await tables.manifests.get(key);

    if (!manifest) {
      throw new Error(`Artifact ${key} is missing`);
    }

    return manifest;
  }
}

export class WebAudioArtifactService {
  private readonly backend: DexieWebAudioArtifactBackend;
  private readonly manager: ArtifactManager<WebAudioArtifactChunk, WebAudioArtifactRef>;

  constructor({ dbRef }: WebAudioArtifactServiceConfig) {
    this.backend = new DexieWebAudioArtifactBackend({ dbRef });
    this.manager = new ArtifactManager({
      backend: this.backend,
    });
  }

  async ensureAudioArtifactStarted({
    audioRecordingId,
    mimeType = MediaRecorderMimeType.WebmOpus,
    metadata,
  }: {
    audioRecordingId: Uuid;
    mimeType?: string;
    metadata?: WebAudioArtifactMetadata;
  }): Promise<void> {
    const snapshot = await this.getAudioArtifactSnapshot({ audioRecordingId });

    if (snapshot) {
      if (snapshot.state === WebAudioArtifactState.Finalized) {
        throw new Error(`Audio artifact ${audioRecordingId} is already finalized`);
      }

      return;
    }

    await this.manager.begin({
      key: audioRecordingId,
      mimeType,
      metadata,
    });
  }

  async appendAudioArtifactChunk({
    audioRecordingId,
    chunk,
  }: {
    audioRecordingId: Uuid;
    chunk: WebAudioArtifactChunk;
  }): Promise<void> {
    await this.ensureAudioArtifactStarted({
      audioRecordingId,
      mimeType: chunk.mimeType,
    });

    await this.manager.append({
      key: audioRecordingId,
      chunk,
    });
  }

  async finalizeAudioArtifact({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): Promise<WebAudioArtifactRef | null> {
    const snapshot = await this.getAudioArtifactSnapshot({ audioRecordingId });

    if (!snapshot) {
      await this.ensureAudioArtifactStarted({ audioRecordingId });
    } else if (snapshot.state === WebAudioArtifactState.Finalized) {
      return snapshot;
    }

    return this.manager.finalize({ key: audioRecordingId });
  }

  async getAudioArtifactSnapshot({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): Promise<WebAudioArtifactSnapshot | null> {
    const snapshot = await this.manager.getSnapshot({ key: audioRecordingId });
    if (!snapshot) {
      return null;
    }

    return {
      ...snapshot,
      state: mapArtifactState({
        state: snapshot.state,
      }),
    };
  }

  async getAudioArtifactRef({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): Promise<WebAudioArtifactRef | null> {
    return this.manager.getRef({ key: audioRecordingId });
  }

  async removeAudioArtifact({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    await this.manager.remove({ key: audioRecordingId });
  }

  async readAudioArtifactBlob({
    artifactRef,
  }: {
    artifactRef: WebAudioArtifactRef;
  }): Promise<Blob | null> {
    const chunkRecords = await this.backend.readChunks({ key: artifactRef.key });

    if (chunkRecords.length === 0) {
      return new Blob([], { type: artifactRef.mimeType });
    }

    return new Blob(
      chunkRecords.map(record => record.rawBlob),
      {
        type: artifactRef.mimeType,
      }
    );
  }

  async getAudioArtifactChunks({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): Promise<LocalWebAudioArtifactChunkRecord[]> {
    return this.backend.readChunks({ key: audioRecordingId });
  }

  async clearAll(): Promise<void> {
    await this.backend.clearAll();
  }
}
